import { useQueryClient } from "@tanstack/react-query";
import { Building2, Clock, History, KeyRound, LayoutDashboard, LogOut, Receipt, type LucideIcon } from "lucide-react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import { post } from "@/lib/api";
import { useMe } from "@/lib/me";
import { cn } from "@/lib/utils";

const NAV: { to: string; label: string; icon: LucideIcon; end?: boolean }[] = [
  { to: "/", label: "Übersicht", icon: LayoutDashboard, end: true },
  { to: "/firmen", label: "Firmen", icon: Building2 },
  { to: "/buchungen", label: "Buchungen", icon: Clock },
  { to: "/ausgaben", label: "Ausgaben", icon: Receipt },
];

export function Layout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Any 401 here redirects to /login via lib/api.
  const me = useMe();

  async function logout() {
    await post("/api/logout");
    queryClient.clear();
    navigate("/login");
  }

  if (me.isError) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-sm text-muted-foreground">Server nicht erreichbar: {me.error?.message}</p>
        <Button variant="outline" size="sm" onClick={() => me.refetch()}>Erneut versuchen</Button>
      </div>
    );
  }
  if (!me.data) return null;
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-4 sm:gap-6">
          <span className="shrink-0 font-semibold">
            boehler.dev<span className="font-normal text-muted-foreground"> cockpit</span>
          </span>
          {/* Desktop: tabs in the header. Phones get a bottom tab bar (below), within thumb reach. */}
          <nav aria-label="Hauptnavigation" className="flex gap-1 max-sm:hidden">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cn(
                    "flex h-9 shrink-0 items-center rounded-md px-3 text-sm transition-colors duration-150 pointer-coarse:h-11",
                    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <NavLink
            to="/verlauf"
            aria-label="Verlauf"
            className={({ isActive }) =>
              cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "ml-auto shrink-0 pointer-coarse:h-11 max-sm:size-9 max-sm:px-0",
                isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground",
              )
            }
          >
            <History />
            <span className="max-sm:sr-only">Verlauf</span>
          </NavLink>
          <Button asChild variant="ghost" size="icon-sm" className="shrink-0 text-muted-foreground pointer-coarse:size-11 max-sm:size-9">
            <Link to="/passkeys" aria-label="Passkeys" title="Passkeys"><KeyRound /></Link>
          </Button>
          <Button variant="ghost" size="sm" className="shrink-0 pointer-coarse:size-11 max-sm:size-9 max-sm:px-0" onClick={logout} aria-label="Abmelden">
            <LogOut className="sm:hidden" />
            <span className="max-sm:sr-only">Abmelden</span>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 pt-6 pb-28 max-sm:pb-40">
        <Outlet />
      </main>
      <nav
        aria-label="Hauptnavigation"
        className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md sm:hidden"
      >
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              cn("flex h-14 flex-col items-center justify-center gap-0.5 text-[0.7rem]", isActive ? "text-foreground" : "text-muted-foreground")
            }
          >
            <n.icon aria-hidden className="size-5" />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
